import cx from 'classnames';
import { type CSSProperties, type ElementType, forwardRef, type ReactNode, useImperativeHandle, useRef } from 'react';

import { type ForwardRefComponent } from '../../@types/types';
import { type TINTS, type COLORS } from '../../constants';
import Box from '../box';
import { type BoxProps } from '../box/Box';
import * as buttonTheme from '../button/theme.css';
import Icon from '../icon';
import LoadingSpinner from '../loadingSpinner';

import * as theme from './theme.css';

export interface IconButtonProps extends Omit<BoxProps, 'ref' | 'children' | 'className' | 'element' | 'size'> {
  /** The content to display inside the button. */
  children?: ReactNode;
  /** A class name for the button to give custom styles. */
  className?: string;
  /** If true, component will be disabled. */
  disabled?: boolean;
  /** A custom element to be rendered */
  element?: ElementType;
  /** The icon displayed inside the button. */
  icon?: ReactNode;
  /** Size of the button. */
  size?: 'small' | 'regular' | 'medium' | 'large';
  /** The color which the icon should have */
  color?: (typeof COLORS)[number] | 'white';
  /** The tint which the icon should have */
  tint?: (typeof TINTS)[number];
  /** If true, component will be shown in a selected state */
  selected?: boolean;
  /** Type of the button element. */
  type?: string;
  /** If true, component will show a loading spinner instead of icon or children. */
  processing?: boolean;
  /** Custom style properties for the icon */
  iconStyle?: CSSProperties;
}

const IconButton: ForwardRefComponent<IconButtonProps> = forwardRef<HTMLElement, IconButtonProps>(
  (
    {
      children,
      className = '',
      disabled,
      element = 'button',
      icon,
      size = 'medium',
      color = 'neutral',
      tint = 'darkest',
      selected,
      type = 'button',
      processing = false,
      iconStyle,
      ...others
    }: IconButtonProps,
    ref,
  ) => {
    const buttonRef = useRef<HTMLElement>(null);
    useImperativeHandle<HTMLElement | null, HTMLElement | null>(ref, () => buttonRef.current);

    const classNames = cx(
      buttonTheme['button-base'],
      theme['icon-button'],
      theme[`is-${size}`],
      {
        [theme['is-disabled']]: disabled,
        [theme['is-processing']]: processing,
        [theme['is-selected']]: selected,
      },
      className,
    );

    const props: BoxProps = {
      ...others,
      ref: buttonRef,
      className: classNames,
      disabled: element === 'button' ? disabled : undefined,
      element,
      type: element === 'button' ? type : undefined,
      'data-teamleader-ui': 'icon-button',
    };

    if (processing) {
      return (
        <Box {...props} overflow="hidden">
          <LoadingSpinner
            className={theme['spinner']}
            color={color === 'white' ? 'neutral' : color}
            size={size === 'small' ? 'small' : 'medium'}
            tint={tint}
          />
        </Box>
      );
    }

    return (
      <Box {...props}>
        <Icon
          color={color === 'white' ? 'neutral' : color}
          tint={color === 'white' ? 'lightest' : tint}
          style={iconStyle}
        >
          {icon}
        </Icon>
        {children}
      </Box>
    );
  },
);

IconButton.displayName = 'IconButton';

export default IconButton;
